import { vuex_subscribe_module, vuex_store_config } from '@/vuex';
import { load_state_from_local_storage } from '@/resources';
// Imported from the leaf module, not the '@alpitour/resources' barrel: this file
// is evaluated inside an import cycle where the barrel is still mid-initialization.
import { PACKAGE_TYPES } from '@alpitour/resources/package_types';

const vuex_config_object = {
  location: {
    label: '',
    path: '',
    keyd: -1,
  },
  keydsmap: [],
  products: [],
  statics: {},
  keyds: {},
  keyd: '',
  nights: '',
  departure_date: '',
  checkin_date: '',
  checkout_date: '',
  flex_dates: false,
  departure_airports: [],
  last_search: '',
  current_search: '',
  availabilities: {},
  pid: 0,
  days: 1,
  solution_id: '',
  room_ref: '',
  selected_departure_airport_code: '',
  detail_params: null,
  selected_room: {},
  passengers: {},
  quote: {},
  filter_keyd: [],
  filter_accommodation: [],
  filter_rating: [],
  filter_airport: [],
  filter_brand: [],
  filter_availability: false,
  sort: '',
  trigger_search: false,
  offset_scroll_search: 0,
  package_type: PACKAGE_TYPES.FLIGHT_AND_STAY,
  // Once-per-session latch: package-type-switch applies
  // guiSettings.customizations.defaultBookingEngineTab the first time a session
  // lands on the homepage, then leaves the tab the user ended up with alone.
  // Persisted with the session, so a refresh never snaps the tab back.
  default_tab_set: false,

  unset_solution: null, // This is a system to avoid selecting the same solution after it errored out
};

export const serialized_vuex_config_object = JSON.stringify(vuex_config_object);

/**
 * Get the default reset value for any state key.
 * Returns a fresh object/array for reference types.
 * Returns null for unknown keys.
 */
export function get_reset_value(param) {
  switch (param) {
    case 'departure_date':
    case 'keyd':
    case 'nights':
    case 'checkin_date':
    case 'checkout_date':
    case 'last_search':
    case 'current_search':
    case 'solution_id':
    case 'room_ref':
    case 'selected_departure_airport_code':
    case 'sort':
      return '';

    case 'location':
      return { label: '', path: '', keyd: -1 };

    case 'keydsmap':
    case 'products':
    case 'departure_airports':
    case 'filter_keyd':
    case 'filter_accommodation':
    case 'filter_rating':
    case 'filter_airport':
    case 'filter_brand':
      return [];

    case 'statics':
    case 'keyds':
    case 'availabilities':
    case 'selected_room':
    case 'passengers':
    case 'quote':
      return {};

    case 'pid':
    case 'offset_scroll_search':
      return 0;

    case 'days':
      return 1;

    case 'flex_dates':
    case 'filter_availability':
    case 'trigger_search':
    case 'default_tab_set':
      return false;

    case 'detail_params':
      return null;

    case 'package_type':
      return PACKAGE_TYPES.FLIGHT_AND_STAY;

    default:
      return null;
  }
}

const state_config = vuex_store_config(vuex_config_object);

// Computed getter (not persisted in localStorage)
state_config.getters.is_only_stay_package = (state) =>
  state.package_type === PACKAGE_TYPES.ONLY_STAY;

state_config.getters.is_flight_and_stay_package = (state) =>
  state.package_type === PACKAGE_TYPES.FLIGHT_AND_STAY;

// Override package_type mutation to reset dependent state.
// The two flows (flight-and-stay / only-stay) are mutually exclusive:
// switching clears all search + detail data so stale state from the
// other flow can never leak into the current one.
state_config.mutations.package_type = (state, payload) => {
  state.package_type = payload;
  state.location = { label: '', path: '', keyd: -1 };
  state.keydsmap = [];
  state.departure_airports = [];
  state.availabilities = {};
  state.departure_date = '';
  state.checkin_date = '';
  state.checkout_date = '';
  state.products = [];
  state.statics = {};
  state.pid = 0;
  state.days = 1;
  state.solution_id = '';
  state.room_ref = '';
  state.selected_departure_airport_code = '';
  state.detail_params = null;
  state.selected_room = {};
};

// Shared logic: apply a key-value payload to state, filtering to existing keys only.
function apply_to_state(state, payload) {
  if (!payload) return;
  const entries = Object.entries(payload).filter(([key]) => key in state);
  for (const [key, value] of entries) {
    state[key] = value;
  }
}

// Atomic batch mutation: set multiple state keys in a single commit.
state_config.mutations.set_store_values = (state, payload) => {
  apply_to_state(state, payload);
};

// Atomic batch mutation: reset keys to defaults with optional overrides in one commit.
// keys: Array or Set of state key names to reset
// overrides: optional { key: value } pairs applied after defaults
state_config.mutations.reset_store_values = (
  state,
  { keys, overrides = {} } = {}
) => {
  if (!keys) return;
  const values = {};
  for (const key of keys) {
    if (key in overrides) continue;
    values[key] = get_reset_value(key);
  }
  apply_to_state(state, { ...values, ...overrides });
};

const name = 'liveat';
const { state } = state_config;
const module = {
  namespaced: true,
  state: load_state_from_local_storage(name, state_config.state),
  mutations: {
    ...state_config.mutations,

    // aggiorna selected_room
    update_selected_room(state, payload) {
      state.selected_room = payload;
    },

    // aggiunge campi a una voce di statics senza sostituirla: la search ne
    // scrive i campi base, il detail ci aggiunge le immagini della gallery
    update_static(state, { pid, data }) {
      if (!pid) return;
      state.statics = {
        ...state.statics,
        [pid]: {
          ...(state.statics[pid] || {}),
          ...data,
        },
      };
    },
  },
  getters: state_config.getters,
  actions: state_config.actions,
  get_local_storage_submodule() {
    // dichiaro le chiavi che verranno scritte nel local storage
    return {
      location: state_config.state.location,
      keydsmap: state_config.state.keydsmap,
      products: state_config.state.products,
      statics: state_config.state.statics,
      keyds: state_config.state.keyds,
      keyd: state_config.state.keyd,
      nights: state_config.state.nights,
      departure_date: state_config.state.departure_date,
      flex_dates: state_config.state.flex_dates,
      departure_airports: state_config.state.departure_airports,
      last_search: state_config.state.last_search,
      current_search: state_config.state.current_search,
      availabilities: state_config.state.availabilities,
      pid: state_config.state.pid,
      days: state_config.state.days,
      solution_id: state_config.state.solution_id,
      room_ref: state_config.state.room_ref,
      selected_departure_airport_code: state_config.state.selected_departure_airport_code,
      detail_params: state_config.state.detail_params,
      selected_room: state_config.state.selected_room,
      passengers: state_config.state.passengers,
      package_type: state_config.state.package_type,
      default_tab_set: state_config.state.default_tab_set,
      checkin_date: state_config.state.checkin_date,
      checkout_date: state_config.state.checkout_date,
    };
  },
};

export function register_state_module(prototype) {
  // eslint-disable-next-line eqeqeq
  if (prototype.$submodules == undefined) prototype.$submodules = {};
  prototype.$submodules[name] = {
    get_local_storage_submodule: module.get_local_storage_submodule(),
  };
  vuex_subscribe_module(name, state, module);
}
